import React, { useRef, useState } from 'react';
import { GameState, Player } from '../../domain/entities';
import { DataService } from '../../services/DataService';
import { soundService } from '../../services/SoundService';
import { useToast } from '../../contexts/ToastContext';
import { Modal } from '../shared/Modal';
import { MatchSimHelpModal } from '../shared/MatchSimHelpModal';
import { PotentialHelpModal } from '../shared/PotentialHelpModal';

interface DebugScreenProps {
    gameState: GameState;
    onSimulateWeek: () => void;
    onSimulateFourWeeks: () => void;
    onSimulateSeason: () => void;
    onSimulateToCupFinal: () => void;
    onSimulateToSeasonWin: () => void;
    isSimulating: boolean;
    onImportGameState: (newState: GameState) => void;
    onToggleAds: () => void;
    adsEnabled: boolean;
    onToggleAiPundit: () => void;
    isAiPunditEnabled: boolean;
    onToggleShowRatingsOnMatchReport: () => void;
    showRatingsOnMatchReport: boolean;
    onAddMoney: () => void;
    onAddBigMoney: () => void;
    isAiAvailable: boolean;
    onAddElitePlayer: () => Promise<Player | undefined>;
    onAddLegendPlayer: () => Promise<Player | undefined>;
    onGenerateGreatYouthIntake: () => void;
    onBoostTeamMorale: () => void;
    onHealAllInjuries: () => void;
    onToggleDebugFlag: (flag: keyof NonNullable<GameState['debugFlags']>) => void;
    onRestartWithTeamCount: (teamCount: number) => void;
}

export const DebugScreen: React.FC<DebugScreenProps> = ({
    gameState,
    onSimulateWeek,
    onSimulateFourWeeks,
    onSimulateSeason,
    onSimulateToCupFinal,
    onSimulateToSeasonWin,
    isSimulating,
    onImportGameState,
    onToggleAds,
    adsEnabled,
    onToggleAiPundit,
    isAiPunditEnabled,
    onToggleShowRatingsOnMatchReport,
    showRatingsOnMatchReport,
    onAddMoney,
    onAddBigMoney,
    isAiAvailable,
    onAddElitePlayer,
    onAddLegendPlayer,
    onGenerateGreatYouthIntake,
    onBoostTeamMorale,
    onHealAllInjuries,
    onToggleDebugFlag,
    onRestartWithTeamCount,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { addToast } = useToast();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isMatchSimHelpOpen, setIsMatchSimHelpOpen] = useState(false);
    const [isPotentialHelpOpen, setIsPotentialHelpOpen] = useState(false);
    const [teamCountModalOpen, setTeamCountModalOpen] = useState(false);
    const [selectedTeamCount, setSelectedTeamCount] = useState(4);

    const handleClick = async (action: () => void | Promise<any>, message?: string) => {
        soundService.playClick();
        await action();
        if (message) {
            addToast(message, 'success');
        }
    };

    const handleExport = () => {
        handleClick(async () => {
            await DataService.exportFullGameState(gameState);
            addToast('Full game state exported!', 'success');
        });
    };

    const handleImportClick = () => {
        handleClick(() => {
            setIsImportModalOpen(true);
        });
    };

    const handleConfirmImport = () => {
        setIsImportModalOpen(false);
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                const newState = await DataService.importFullGameState(file);
                onImportGameState(newState);
                soundService.playSuccess();
                addToast('Game state imported successfully!', 'success');
            } catch (error: any) {
                soundService.playError();
                addToast(`Import failed: ${error.message}`, 'error');
            }
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <>
            {isSimulating && (
                 <div className="panel" style={{ borderLeftColor: 'var(--warning-color)', marginBottom: '16px' }}>
                    <div className="panel-header" style={{ color: 'var(--warning-color)' }}>
                        Simulation in Progress...
                    </div>
                    <div className="panel-content">
                        <p><strong>Please wait. This may take a moment. The UI will be unresponsive until the simulation is complete.</strong></p>
                    </div>
                </div>
            )}
            
            <div className="panel" style={{ borderLeftColor: 'var(--info-color)' }}>
                <div className="panel-header" style={{ color: 'var(--info-color)' }}>
                    Simulation Controls ⏩
                </div>
                <div className="panel-content">
                    <p>Advance the game by one or more weeks, or to the end of the season.</p>
                    <div className="debug-grid">
                        <button className="button" onClick={() => handleClick(onSimulateWeek)} disabled={isSimulating}>
                            Simulate 1 Week
                        </button>
                        <button className="button" onClick={() => handleClick(onSimulateFourWeeks)} disabled={isSimulating}>
                            Simulate 4 Weeks
                        </button>
                        <button className="button" onClick={() => handleClick(onSimulateSeason)} disabled={isSimulating}>
                            Simulate Full Season
                        </button>
                        <button className="button" onClick={() => handleClick(onSimulateToCupFinal)} disabled={isSimulating}>
                            Simulate Cup Final
                        </button>
                        <button className="button" onClick={() => handleClick(onSimulateToSeasonWin)} disabled={isSimulating}>
                            Simulate Win Season
                        </button>
                    </div>
                </div>
            </div>

            <div className="panel">
                <div className="panel-header">
                    Feature Toggles 🎛️
                </div>
                <div className="panel-content">
                    <p>Enable or disable experimental or optional features.</p>

                    <div style={{
                        border: `1px solid ${isAiAvailable ? 'var(--positive-color)' : 'var(--warning-color)'}`,
                        backgroundColor: `rgba(${isAiAvailable ? '0, 255, 155' : '255, 193, 7'}, 0.1)`,
                        padding: '12px',
                        margin: '16px 0',
                        textAlign: 'center'
                    }}>
                        {isAiAvailable ? (
                            <p className="positive" style={{ margin: 0 }}><strong>✅ Gemini AI Ready:</strong> API Key is configured. AI Pundit available.</p>
                        ) : (
                            <p style={{ margin: 0, color: 'var(--warning-color)' }}><strong>⚠️ Gemini AI Offline:</strong> No API Key configured. AI Pundit disabled.</p>
                        )}
                    </div>

                    <div className="debug-grid">
                        <button className="button" onClick={() => handleClick(onToggleAiPundit)} disabled={!isAiAvailable}>
                            AI Pundit ({isAiPunditEnabled ? 'Enabled' : 'Disabled'})
                        </button>
                        <button className="button" onClick={() => handleClick(onToggleAds)}>
                            Ads ({adsEnabled ? 'Enabled' : 'Disabled'})
                        </button>
                        <button className="button" onClick={() => handleClick(onToggleShowRatingsOnMatchReport)}>
                            Match Ratings ({showRatingsOnMatchReport ? 'Shown' : 'Hidden'})
                        </button>
                        <button className="button" onClick={() => handleClick(() => onToggleDebugFlag('alwaysWin'))}>
                            Always Win ({gameState.debugFlags?.alwaysWin ? 'Enabled' : 'Disabled'})
                        </button>
                        <button className="button" onClick={() => handleClick(() => onToggleDebugFlag('showHiddenInfo'))}>
                            Hidden Info Card ({gameState.debugFlags?.showHiddenInfo ? 'Enabled' : 'Disabled'})
                        </button>
                    </div>
                </div>
            </div>

            <div className="panel" style={{ borderLeftColor: 'var(--positive-color)' }}>
                <div className="panel-header" style={{ color: 'var(--positive-color)' }}>
                    Game State Cheats 💰
                </div>
                <div className="panel-content">
                    <p>Directly modify your game state for testing purposes.</p>
                     <div className="debug-grid">
                        <button className="button" onClick={() => handleClick(onAddMoney, 'Added £1,000,000 to your balance!')} disabled={isSimulating}>
                            Add £1,000,000
                        </button>
                        <button className="button" onClick={() => handleClick(onAddBigMoney, 'Added £100,000,000 to your balance!')} disabled={isSimulating}>
                            Add £100,000,000
                        </button>
                        <button className="button" onClick={async () => {
                            soundService.playClick();
                            const newPlayer = await onAddElitePlayer();
                            if (newPlayer) {
                                addToast(`${newPlayer.faceEmoji} ${newPlayer.name} has joined your squad!`, 'success');
                            } else {
                                addToast('Failed to add elite player. Squad might be full.', 'error');
                            }
                        }} disabled={isSimulating}>
                            Add Elite Player
                        </button>
                        <button className="button" onClick={async () => {
                            soundService.playClick();
                            const newPlayer = await onAddLegendPlayer();
                            if (newPlayer) {
                                addToast(`${newPlayer.faceEmoji} ${newPlayer.name} has joined your club!`, 'success');
                            } else {
                                addToast('Failed to add legend player. Squad might be full.', 'error');
                            }
                        }} disabled={isSimulating}>
                            Add Legend Player
                        </button>
                        <button className="button" onClick={() => handleClick(onGenerateGreatYouthIntake, 'A "golden generation" has arrived at the academy!')} disabled={isSimulating}>
                            Generate Great Youth Intake
                        </button>
                        <button className="button" onClick={() => handleClick(onBoostTeamMorale, 'Team morale set to Ecstatic!')} disabled={isSimulating}>
                            Boost Team Morale
                        </button>
                        <button className="button" onClick={() => handleClick(onHealAllInjuries, 'All injuries have been healed!')} disabled={isSimulating}>
                            Heal All Injuries
                        </button>
                    </div>
                </div>
            </div>

            <div className="panel">
                <div className="panel-header">
                    Data Management 💾
                </div>
                <div className="panel-content">
                    <p>Export your current game for bug reporting or import a saved state.</p>
                    <div className="debug-grid">
                        <button className="button" onClick={handleExport} disabled={isSimulating}>
                            Export Full Game State
                        </button>
                        <button className="button" onClick={handleImportClick} disabled={isSimulating}>
                            Import Full Game State
                        </button>
                    </div>
                </div>
            </div>

            <div className="panel" style={{ borderLeftColor: 'var(--info-color)' }}>
                <div className="panel-header" style={{ color: 'var(--info-color)' }}>
                    New Game Options 🎮
                </div>
                <div className="panel-content">
                    <p>Restart the game with specific settings. This will start a completely new game.</p>
                    <div className="debug-grid">
                        <button className="button" onClick={() => handleClick(() => setTeamCountModalOpen(true))} disabled={isSimulating}>
                            Restart with Limited Teams
                        </button>
                    </div>
                </div>
            </div>

             <div className="panel" style={{ borderLeftColor: 'var(--info-color)' }}>
                <div className="panel-header" style={{ color: 'var(--info-color)' }}>
                    Internal Info & Help ℹ️
                </div>
                <div className="panel-content">
                    <p>View detailed explanations of internal game mechanics.</p>
                    <div className="debug-grid">
                        <button className="button" onClick={() => handleClick(() => setIsMatchSimHelpOpen(true))}>
                            Match Simulator Explained
                        </button>
                        <button className="button" onClick={() => handleClick(() => setIsPotentialHelpOpen(true))}>
                            Player Progression Explained
                        </button>
                    </div>
                </div>
            </div>

            <div className="panel">
                <div className="panel-header">
                    Sound Testing 🔊
                </div>
                <div className="panel-content">
                    <p>Test each sound effect from the SoundService.</p>
                    <div className="debug-grid">
                        <button className="button" onClick={() => { soundService.playClick(); }}>
                            Click
                        </button>
                        <button className="button" onClick={() => { soundService.playNavigate(); }}>
                            Navigate
                        </button>
                        <button className="button" onClick={() => { soundService.playSuccess(); }}>
                            Success
                        </button>
                        <button className="button" onClick={() => { soundService.playError(); }}>
                            Error
                        </button>
                        <button className="button" onClick={() => { soundService.playAdvance(); }}>
                            Advance
                        </button>
                        <button className="button" onClick={() => { soundService.playGoal(); }}>
                            Goal
                        </button>
                        <button className="button" onClick={() => { soundService.playWhistle(); }}>
                            Whistle
                        </button>
                        <button className="button" onClick={() => { soundService.playCard(); }}>
                            Card
                        </button>
                        <button className="button" onClick={() => { soundService.playKick(); }}>
                            Kick
                        </button>
                    </div>
                </div>
            </div>

            <div className="panel" style={{ borderLeftColor: 'var(--negative-color)' }}>
                <div className="panel-header" style={{ color: 'var(--negative-color)' }}>
                    Danger Zone ⚠️
                </div>
                <div className="panel-content">
                    <p>Actions that test error handling or may cause issues.</p>
                    <div className="debug-grid">
                        <button className="button danger" onClick={() => { throw new Error('This is a test error for Sentry!') }} disabled={isSimulating}>
                            Throw Test Error (Sentry)
                        </button>
                    </div>
                </div>
            </div>

            <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} accept=".json,.json.gz,.gz,.zip" />

            <Modal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)}>
                <h2>Import Game State?</h2>
                <p className="negative">Warning: This will completely overwrite your current game session. Any unsaved progress will be lost. Are you sure you want to continue?</p>
                <div className="modal-actions">
                    <button className="button" onClick={() => setIsImportModalOpen(false)}>Cancel</button>
                    <button className="button danger" onClick={handleConfirmImport}>Confirm & Import</button>
                </div>
            </Modal>
            
            <MatchSimHelpModal isOpen={isMatchSimHelpOpen} onClose={() => setIsMatchSimHelpOpen(false)} />
            <PotentialHelpModal isOpen={isPotentialHelpOpen} onClose={() => setIsPotentialHelpOpen(false)} />

            <Modal isOpen={teamCountModalOpen} onClose={() => setTeamCountModalOpen(false)}>
                <h2>Restart with Limited Teams</h2>
                <p className="warning">This will start a completely new game with a restricted number of teams per league (randomly selected). Your current progress will be lost.</p>
                <p><strong>Select teams per league:</strong></p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', margin: '16px 0' }}>
                    {[4, 5, 6, 7, 8].map(count => (
                        <button
                            key={count}
                            className="button"
                            style={{
                                backgroundColor: selectedTeamCount === count ? 'var(--accent-color)' : 'var(--secondary-bg-color)',
                                color: selectedTeamCount === count ? '#000' : 'var(--text-color)'
                            }}
                            onClick={() => setSelectedTeamCount(count)}
                        >
                            {count}
                        </button>
                    ))}
                </div>
                <p style={{ fontSize: '0.9em', color: 'var(--secondary-text-color)' }}>
                    This will randomly select {selectedTeamCount} teams from each league to create a smaller, more intimate league structure.
                </p>
                <div className="modal-actions">
                    <button className="button" onClick={() => setTeamCountModalOpen(false)}>Cancel</button>
                    <button className="button danger" onClick={async () => {
                        soundService.playClick();
                        setTeamCountModalOpen(false);
                        addToast(`Restarting game with ${selectedTeamCount} teams per league...`, 'info');
                        await onRestartWithTeamCount(selectedTeamCount);
                    }}>Confirm & Restart</button>
                </div>
            </Modal>
        </>
    );
};